import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { markLessonAsCompleted, isLessonCompleted } from '../services/lessonService';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { ArrowBack, RadioButtonUnchecked, Circle as CircleIcon, CheckCircle } from '@mui/icons-material';

// Import our custom components
import KeyStatistics from '../components/KeyStatistics';
import QuickKnowledgeCheck from '../components/QuickKnowledgeCheck';
import HighRiskFactorsGuide from '../components/HighRiskFactorsGuide';
import RiskFactorsTriad from '../components/RiskFactorsTriad';
import EmotionalImpactVisual from '../components/EmotionalImpactVisual';
import AssistiveDevicesSection from '../components/AssistiveDevicesSection';

// Import video assets
import causesOfFallsVideo from '../assets/causes_of_falls.mp4';
import impactOfFallsVideo from '../assets/Sample 22_impact_of_falls.mp4';
import highRiskFactorsVideo from '../assets/Sample 22_High-Risk_Factors_for_Falls.mp4';
import riskCategoriesVideo from '../assets/Sample 22_Physical_Environmental_and_Medication_Related_Risks.mp4';
import psychologicalImpactVideo from '../assets/Sample 22_Psychological_and_Emotional Impact of Falls.mp4';
import conductingAssessmentVideo from '../assets/Sample 22_conducting_fall_assessment.mp4';
import environmentalHazardsVideo from '../assets/videos/Sample 22_Identifying_and_Eliminating_Environmental_Hazards.mp4';
import medicationReviewVideo from '../assets/Sample 22_medicalreview-2.mp4';
import standardAssessmentVideo from '../assets/Sample 22_Standard_Assessment_Tests.mp4';
import safeTransfersVideo from '../assets/Sample 22_Safe_Techniques_for_Transfers_and_Ambulation.mp4';
import supportingIndependenceVideo from '../assets/Sample 22_Supporting_Independence_While_Ensuring_Safety.mp4';
import bodyMechanicsVideo from '../assets/Sample 22_Proper_Body_Mechanics_for_Caregivers copy.mp4';
import effectiveCommunicationVideo from '../assets/Sample 22_Effective_Communication_to_Reduce_Fear_of_Falling.mp4';
import immediateResponseVideo from '../assets/Sample 22_Immediate_Response_Protocol.mp4';
import monitoring from '../assets/Sample 22_monitoring_protocols.mp4'
// --- Import Lesson Data --- 
// TODO: Replace this with a proper data fetching mechanism (e.g., API call or context)
import { 
  module1_lessons, 
  module2_lessons, 
  module3_lessons, 
  module4_lessons 
} from './FallPreventionCourse.js'; // Adjust path if necessary

// Debug the imported modules
console.log('Imported modules:', {
  module1_length: module1_lessons.length,
  module2_length: module2_lessons.length,
  module3_length: module3_lessons.length,
  module4_length: module4_lessons.length
});

// Log specific lessons to debug
console.log('Module 2 lessons:', module2_lessons.map(l => ({ id: l.id, title: l.title, isHeader: !!l.isHeader })));

// Combine all lessons into one lookup object (or fetch dynamically)
const allLessonsData = {
  introduction: module1_lessons,
  assessment: module2_lessons,
  intervention: module3_lessons,
  emergency: module4_lessons.filter(lesson => !lesson.isHeader || !lesson.title.includes('Technology')),
  technology: module4_lessons.filter(lesson => lesson.isHeader && lesson.title.includes('Technology') || !lesson.isHeader && lesson.id.startsWith('monitoring')),
};

// Create a direct lesson lookup for faster access
const allLessonsLookup = {};

// Flatten all lessons into a single lookup by ID
Object.values(allLessonsData).forEach(moduleArray => {
  if (!moduleArray || !Array.isArray(moduleArray)) {
    console.error('Invalid module array:', moduleArray);
    return;
  }
  
  moduleArray.forEach(lesson => {
    if (!lesson.isHeader && lesson.id) {
      // Store the lesson by ID for direct lookup
      allLessonsLookup[lesson.id] = lesson;
    }
  });
});

// Debug available lessons
console.log('Available lessons by ID:', Object.keys(allLessonsLookup));

// Helper function to get module title
const getModuleTitle = (moduleId) => {
  const moduleTitles = {
    'introduction': 'Introduction & Understanding Risks',
    'assessment': 'Assessment & Prevention Strategies',
    'intervention': 'Intervention & Caregiver Practices',
    'emergency': 'Emergency Response & Post-Fall Care',
    'technology': 'Technology & Innovations in Fall Prevention'
  };
  return moduleTitles[moduleId] || 'Module';
};

// Helper function to find a lesson by ID across all modules
const findLessonById = (moduleId, lessonId) => {
  console.log('Finding lesson:', { moduleId, lessonId });
  console.log('Available modules:', Object.keys(allLessonsData));
  
  // First check direct lookup for fastest access
  if (allLessonsLookup[lessonId]) {
    console.log('Found lesson in direct lookup:', allLessonsLookup[lessonId]);
    return allLessonsLookup[lessonId];
  }
  
  // Then try to find the lesson in the specified module
  const moduleLessons = allLessonsData[moduleId];
  console.log('Module lessons count:', moduleLessons ? moduleLessons.length : 0);
  
  if (moduleLessons && Array.isArray(moduleLessons)) {
    // Find the lesson, skipping headers
    const foundLesson = moduleLessons.find(lesson => !lesson.isHeader && lesson.id === lessonId);
    if (foundLesson) {
      console.log('Found lesson in specified module:', foundLesson);
      return foundLesson;
    }
  }
  
  // If still not found, search through all modules
  for (const [moduleKey, lessons] of Object.entries(allLessonsData)) {
    if (moduleKey !== moduleId && Array.isArray(lessons)) { // Skip the already checked module
      const foundLesson = lessons.find(lesson => !lesson.isHeader && lesson.id === lessonId);
      if (foundLesson) {
        console.log(`Found lesson in different module (${moduleKey}):`, foundLesson);
        return foundLesson;
      }
    }
  }
  
  // Special case for module2_lessons - direct check
  console.log('Checking module2_lessons directly');
  const directModule2Check = module2_lessons.find(lesson => !lesson.isHeader && lesson.id === lessonId);
  if (directModule2Check) {
    console.log('Found lesson in direct module2_lessons check:', directModule2Check);
    return directModule2Check;
  }
  
  console.log('Lesson not found in any module');
  return null;
};

// Helper function to get all non-header lessons for the sidebar
const getModuleLessonsForSidebar = (moduleId) => {
  const moduleLessons = allLessonsData[moduleId];
  if (!moduleLessons) return [];
  return moduleLessons.filter(lesson => !lesson.isHeader);
};

// Mock Components for Rendering (Replace with actual implementations)
const LessonSidebar = ({ moduleTitle, lessons, currentLessonId, moduleId }) => {
  const navigate = useNavigate();
  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{moduleTitle}</Typography>
      <List dense>
        {lessons.map((lesson, index) => (
          <ListItem 
            key={lesson.id} 
            button 
            selected={lesson.id === currentLessonId}
            onClick={() => navigate(`/module-content/${moduleId}/${lesson.id}`)}
          >
            <ListItemIcon sx={{ minWidth: '30px' }}>
              {/* Basic check icon for demo */}
              {isLessonCompleted(lesson.id) ? <CheckCircle fontSize="small" color="success" /> : <RadioButtonUnchecked fontSize="small" color="disabled" />}
            </ListItemIcon>
            <ListItemText primary={`${index + 1}. ${lesson.title}`} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

const RiskFactorSection = ({ factors }) => (
  <Box sx={{ my: 4 }}>
    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>Fall Risk Factors</Typography>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card elevation={1} sx={{ 
          height: '100%',
          borderLeft: '4px solid',
          borderColor: 'primary.main',
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 3
          }
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
              Intrinsic Factors
              <Box component="span" sx={{ display: 'block', fontSize: '0.9rem', fontWeight: 400, mt: 0.5, color: 'text.secondary' }}>
                Physical and health-related factors
              </Box>
            </Typography>
            <List disablePadding>
              {factors?.intrinsic?.map((factor, i) => (
                <ListItem key={i} sx={{ py: 0.75, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary={factor} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card elevation={1} sx={{ 
          height: '100%',
          borderLeft: '4px solid',
          borderColor: 'primary.main',
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 3
          }
        }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
              Extrinsic Factors
              <Box component="span" sx={{ display: 'block', fontSize: '0.9rem', fontWeight: 400, mt: 0.5, color: 'text.secondary' }}>
                Environmental and external factors
              </Box>
            </Typography>
            <List disablePadding>
              {factors?.extrinsic?.map((factor, i) => (
                <ListItem key={i} sx={{ py: 0.75, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary={factor} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

const AssessmentFrameworkSection = ({ framework }) => (
  <Box sx={{ my: 4 }}>
    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>{framework?.title}</Typography>
    <Typography variant="body2" paragraph color="text.secondary" sx={{ mb: 3 }}>{framework?.description}</Typography>
    <Box sx={{ 
      p: 3, 
      borderRadius: 2, 
      bgcolor: 'background.paper',
      boxShadow: 1,
      border: '1px solid',
      borderColor: 'divider',
      borderLeft: '4px solid',
      borderLeftColor: 'primary.main'
    }}>
      {framework?.steps?.map((step, i) => (
        <Box key={i} sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          mb: i < framework.steps.length - 1 ? 2.5 : 0 
        }}>
          <Box sx={{ 
            minWidth: 32, 
            height: 32, 
            borderRadius: '50%', 
            bgcolor: 'primary.main', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            mr: 2,
            fontSize: '0.9rem'
          }}>
            {i+1}
          </Box>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{step}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const EvidenceInsightsSection = ({ insightsData }) => (
  <Box sx={{ my: 4 }}>
    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>{insightsData?.title}</Typography>
    <Typography variant="body2" paragraph color="text.secondary" sx={{ mb: 2.5 }}>{insightsData?.description}</Typography>
    <Box sx={{ 
      bgcolor: 'background.paper',
      px: 3,
      py: 2.5,
      borderRadius: 2,
      boxShadow: 1,
      borderLeft: '4px solid',
      borderLeftColor: 'primary.main'
    }}>
      {insightsData?.insights?.map((insight, i) => (
        <Box key={i} sx={{ 
          p: 1.5, 
          mb: i < insightsData.insights.length - 1 ? 1.5 : 0,
          borderBottom: i < insightsData.insights.length - 1 ? '1px solid' : 'none',
          borderColor: 'divider'
        }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary' }}>{insight}</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const HighRiskPopulationSection = ({ populationData }) => (
  <Box sx={{ my: 4 }}>
    <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>{populationData?.title}</Typography>
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {populationData?.groups?.map((group, i) => (
        <Grid item xs={12} md={6} key={i}>
          <Card elevation={1} sx={{ 
            p: 3, 
            height: '100%', 
            borderRadius: 2,
            boxShadow: 1,
            bgcolor: 'background.paper',
            borderLeft: '4px solid',
            borderLeftColor: 'primary.main',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: 3
            }
          }}>
            <Typography variant="h6" sx={{ 
              color: 'primary.main',
              fontWeight: 600,
              mb: 2
            }}>
              {group.name}
            </Typography>
            <List disablePadding>
              {group.points?.map((point, j) => (
                <ListItem 
                  key={j} 
                  alignItems="flex-start" 
                  sx={{ 
                    py: 1,
                    px: 0,
                    '&:last-child': {
                      pb: 0
                    }
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 28,
                      mt: 0.75,
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main',
                        mt: 0.5
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={point} 
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      sx: { 
                        lineHeight: 1.6,
                        mt: 0
                      }
                    }} 
                  />
                </ListItem>
              ))}
            </List>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

// Component to handle the sections structure for Impact of Falls lesson
const SectionsComponent = ({ sections }) => (
  <Box sx={{ my: 4 }}>
    <Grid container spacing={3}>
      {sections.map((section, i) => (
        <Grid item xs={12} key={i}>
          <Card elevation={1} sx={{ 
            p: 3,
            borderRadius: 2,
            boxShadow: 1,
            bgcolor: 'background.paper',
            borderLeft: '4px solid',
            borderLeftColor: 'primary.main',
            transition: 'all 0.3s ease',
            mb: 2,
            '&:hover': {
              boxShadow: 3
            }
          }}>
            <Typography variant="h6" sx={{ 
              color: 'primary.main',
              fontWeight: 600,
              mb: 2
            }}>
              {section.title}
            </Typography>
            <List disablePadding>
              {section.points.map((point, j) => (
                <ListItem 
                  key={j} 
                  alignItems="flex-start" 
                  sx={{ 
                    py: 1,
                    px: 0,
                    '&:last-child': {
                      pb: 0
                    }
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: 28,
                      mt: 0.75,
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main',
                        mt: 0.5
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={point} 
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      sx: { 
                        lineHeight: 1.6,
                        mt: 0
                      }
                    }} 
                  />
                </ListItem>
              ))}
            </List>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const ModuleContentPage = () => {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarLessons, setSidebarLessons] = useState([]);
  const [moduleTitle, setModuleTitle] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    console.log('ModuleContentPage mounted with:', { moduleId, lessonId });
    
    // Debug the modules content
    console.log('Module2 lessons IDs:', module2_lessons.filter(l => !l.isHeader).map(l => l.id));
    
    // Find the lesson using our helper function
    const foundLesson = findLessonById(moduleId, lessonId);
    console.log('Found lesson:', foundLesson);
    
    if (foundLesson) {
      setLesson(foundLesson);
      setSidebarLessons(getModuleLessonsForSidebar(moduleId));
      setModuleTitle(getModuleTitle(moduleId));
      setIsCompleted(isLessonCompleted(lessonId));
    } else {
      // If lesson not found in the current module, check if it exists in any module
      const lessonInAnyModule = Object.values(allLessonsLookup).find(l => l.id === lessonId);
      if (lessonInAnyModule) {
        console.log('Lesson found in lookup but not in module:', lessonInAnyModule);
        setLesson(lessonInAnyModule);
        setSidebarLessons(getModuleLessonsForSidebar(moduleId));
        setModuleTitle(getModuleTitle(moduleId));
        setIsCompleted(isLessonCompleted(lessonId));
      }
    }
    setLoading(false);
  }, [moduleId, lessonId]);

  const handleComplete = () => {
    markLessonAsCompleted(lessonId);
    setIsCompleted(true);
    // Maybe navigate to next lesson or back to course?
    // Example: Navigate back to the main course page
    navigate(`/courses/fall-prevention`); // Adjust if course path is different
  };

  const handleBackClick = () => {
    // Navigate back to the main course page view
    navigate(`/courses/fall-prevention`); // Adjust if course path is different
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!lesson) {
  // Check if the lesson exists in any module
  const availableLessons = Object.keys(allLessonsLookup).join(', ');
  
  return (
      <Container sx={{ mt: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBackClick} sx={{ mb: 2 }}>
          Back to Course
        </Button>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h5" color="error" gutterBottom>Lesson Not Found</Typography>
          <Typography paragraph>The requested lesson ({lessonId}) could not be found in module {moduleId}.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Available lesson IDs: {availableLessons || 'None'}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 3 }}
            onClick={handleBackClick}
          >
            Return to Course
          </Button>
        </Paper>
      </Container>
    );
  }

  // --- Main Rendering Logic --- 
  return (
    <Container maxWidth="xl" sx={{ mt: 5, mb: 5 }}>
      <Button 
        startIcon={<ArrowBack />} 
        onClick={handleBackClick} 
        sx={{ 
          mb: 4,
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          }
        }}
      >
        Back to Learning Modules
      </Button>
      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <LessonSidebar 
            moduleTitle={moduleTitle}
            lessons={sidebarLessons}
            currentLessonId={lessonId}
            moduleId={moduleId}
          />
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={9}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 3,
              minHeight: '80vh',
              background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                color: 'primary.main',
                mb: 3,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                letterSpacing: '-0.02em'
              }}
            >
              {lesson.title} 
            </Typography>
            <Typography 
              variant="subtitle1" 
              paragraph 
              color="text.secondary" 
              sx={{ 
                mb: 4, 
                maxWidth: '100%',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                letterSpacing: '0.01em'
              }}
            >
              {lesson.description}
          </Typography>
            <Divider sx={{ 
              my: 4,
              borderColor: 'divider',
              borderWidth: '1px'
            }}/>
            
            {/* Render structured content based on lessonData.content */}
            {typeof lesson.content === 'object' && lesson.content !== null ? (
              <Box sx={{ width: '100%' }}>
                {/* Key Statistics */}
                {lesson.content.keyStats && (
                  <Box sx={{ mb: 6 }}>
                    <KeyStatistics 
                      stats={lesson.content.keyStats}
                      highlights={[
                        
                      ]}
                    />
                  </Box>
                )}
                
                {/* Video Content */}
                {lesson.content.videoId && (
                  <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Video Demonstration
                    </Typography>
                    <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ position: 'relative', width: '100%', pt: '56.25%' }}>
                        <Box
                          component="video"
                          controls
                          disablePictureInPicture
                          controlsList="nodownload nofullscreen"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          src={
                            lesson.content.videoId === 'causes_of_falls' ? causesOfFallsVideo :
                            lesson.content.videoId === 'impact_of_falls' ? impactOfFallsVideo :
                            lesson.content.videoId === 'high_risk_factors' ? highRiskFactorsVideo :
                            lesson.content.videoId === 'risk_categories' ? riskCategoriesVideo :
                            lesson.content.videoId === 'psychological_impact' ? psychologicalImpactVideo :
                            lesson.content.videoId === 'conducting_assessment' ? conductingAssessmentVideo :
                            lesson.content.videoId === 'environmental_hazards' ? environmentalHazardsVideo :
                            lesson.content.videoId === 'medication_review' ? medicationReviewVideo :
                            lesson.content.videoId === 'standard_assessment' ? standardAssessmentVideo :
                            lesson.content.videoId === 'safe_transfers' ? safeTransfersVideo :
                            lesson.content.videoId === 'supporting_independence' ? supportingIndependenceVideo :
                            lesson.content.videoId === 'body_mechanics' ? bodyMechanicsVideo :
                            lesson.content.videoId === 'effective_communication' ? effectiveCommunicationVideo :
                            lesson.content.videoId === 'fall_response_protocol' ? immediateResponseVideo :
                            lesson.content.videoId === 'monitoring_protocols' ? monitoring : // Temporarily using immediateResponseVideo
                            null
                          }
                        />
                      </Box>
                      {lesson.content.videoCaption && (
                        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                          <Typography variant="body2" color="text.secondary">
                            {lesson.content.videoCaption}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Box>
                )}

                {/* Risk Factors */}
                {lesson.content.riskFactors && (
                  <Box sx={{ mb: 6 }}>
                    <RiskFactorSection factors={lesson.content.riskFactors} />
                  </Box>
                )}

                {/* Assessment Framework */}
                {lesson.content.assessmentFramework && (
                  <Box sx={{ mb: 6 }}>
                    <AssessmentFrameworkSection framework={lesson.content.assessmentFramework} />
                  </Box>
                )}

                {/* Evidence Insights */}
                {lesson.content.evidenceInsights && (
                  <Box sx={{ mb: 6 }}>
                    <EvidenceInsightsSection insightsData={lesson.content.evidenceInsights} />
                  </Box>
                )}

                {/* High Risk Populations */}
                {lesson.content.highRiskPopulations && (
                  <Box sx={{ mb: 6 }}>
                    <HighRiskPopulationSection populationData={lesson.content.highRiskPopulations} />
                  </Box>
                )}
                
                {/* Sections - for Impact of Falls and similar lessons */}
                {lesson.content.sections && (
                  <Box sx={{ mb: 6 }}>
                    <SectionsComponent sections={lesson.content.sections} />
                  </Box>
                )}
                
                {/* Visual Guide - for High-Risk Factors */}
                {lesson.content.visualGuide && lesson.content.visualGuide.component === "HighRiskFactorsGuide" && (
                  <Box sx={{ mb: 6 }}>
                    <HighRiskFactorsGuide />
                  </Box>
                )}
                
                {/* Visual Guide - for Risk Factors Triad */}
                {lesson.content.visualGuide && lesson.content.visualGuide.component === "RiskFactorsTriad" && (
                  <Box sx={{ mb: 6 }}>
                    <RiskFactorsTriad />
                  </Box>
                )}
                
                {/* Visual Guide - for Emotional Impact */}
                {lesson.content.visualGuide && lesson.content.visualGuide.component === "EmotionalImpactVisual" && (
                  <Box sx={{ mb: 6 }}>
                    <EmotionalImpactVisual />
                  </Box>
                )}
                
                {/* Assistive Devices Section */}
                {lesson.content.commonDevices && (
                  <Box sx={{ mb: 6 }}>
                    <AssistiveDevicesSection devicesData={lesson.content} />
                  </Box>
                )}
                
                {/* Response Steps Section */}
                {lesson.content.responseSteps && (
                  <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {lesson.content.responseSteps.title}
                    </Typography>
                    <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <CardContent sx={{ p: 3 }}>
                        <List sx={{ width: '100%' }}>
                          {lesson.content.responseSteps.steps.map((step, index) => (
                            <Box key={index} sx={{ mb: index < lesson.content.responseSteps.steps.length - 1 ? 3 : 0 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {index + 1}. {step.step}
                              </Typography>
                              <List sx={{ pl: 2 }}>
                                {step.details.map((detail, detailIndex) => (
                                  <ListItem key={detailIndex} sx={{ py: 0.5, px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={detail} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Box>
                )}
                
                {/* Addressing Challenges Section */}
                {lesson.content.addressingChallenges && (
                  <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {lesson.content.addressingChallenges.title}
                    </Typography>
                    <Grid container spacing={3}>
                      {lesson.content.addressingChallenges.challenges.map((challenge, index) => (
                        <Grid item xs={12} md={4} key={index}>
                          <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {challenge.challenge}
                              </Typography>
                              <List sx={{ pl: 0 }}>
                                {challenge.strategies.map((strategy, strategyIndex) => (
                                  <ListItem key={strategyIndex} sx={{ py: 0.5, px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={strategy} />
                                  </ListItem>
                                ))}
                              </List>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                
                {/* Motivational Techniques Section */}
                {lesson.content.motivationalTechniques && (
                  <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                      {lesson.content.motivationalTechniques.title}
                    </Typography>
                    <Grid container spacing={3}>
                      {lesson.content.motivationalTechniques.techniques.map((technique, index) => (
                        <Grid item xs={12} md={6} key={index}>
                          <Card 
                            elevation={2} 
                            sx={{ 
                              height: '100%', 
                              borderRadius: 2,
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 6
                              }
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Typography 
                                variant="h6" 
                                gutterBottom 
                                sx={{ 
                                  fontWeight: 600, 
                                  color: 'primary.main',
                                  pb: 1,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                {technique.technique}
                              </Typography>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  color: 'text.primary', 
                                  my: 2,
                                  fontStyle: 'italic'
                                }}
                              >
                                {technique.description}
                              </Typography>
                              <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                  Examples:
                                </Typography>
                                <List sx={{ pl: 0 }}>
                                  {technique.examples.map((example, exampleIndex) => (
                                    <ListItem key={exampleIndex} sx={{ py: 0.5, px: 0 }}>
                                      <ListItemIcon sx={{ minWidth: 36 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                      </ListItemIcon>
                                      <ListItemText 
                                        primary={example}
                                        primaryTypographyProps={{
                                          variant: 'body2',
                                          sx: { fontWeight: 500 }
                                        }} 
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                
                {/* Monitoring Systems Section */}
                {lesson.content.monitoringSystems && (
                  <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {lesson.content.monitoringSystems.title}
                    </Typography>
                    <Grid container spacing={3}>
                      {lesson.content.monitoringSystems.components.map((component, index) => (
                        <Grid item xs={12} md={6} key={index}>
                          <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {component.component}
                              </Typography>
                              <List sx={{ pl: 0 }}>
                                {component.practices.map((practice, practiceIndex) => (
                                  <ListItem key={practiceIndex} sx={{ py: 0.5, px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={practice} />
                                  </ListItem>
                                ))}
                              </List>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                
                {/* Individualized Plans Section */}
                {lesson.content.individualizedPlans && (
                  <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {lesson.content.individualizedPlans.title}
                    </Typography>
                    <Grid container spacing={3}>
                      {lesson.content.individualizedPlans.strategies.map((strategy, index) => (
                        <Grid item xs={12} md={6} key={index}>
                          <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {strategy.goal}
                              </Typography>
                              <List sx={{ pl: 0 }}>
                                {strategy.methods.map((method, methodIndex) => (
                                  <ListItem key={methodIndex} sx={{ py: 0.5, px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={method} />
                                  </ListItem>
                                ))}
                              </List>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Knowledge Check */}
                {lesson.content.knowledgeCheck && (
                  <Box sx={{ mb: 6 }}>
                    <QuickKnowledgeCheck 
                      questions={lesson.content.knowledgeCheck.questions}
                    />
                  </Box>
                )}
          </Box>
            ) : (
              <Typography sx={{ 
                whiteSpace: 'pre-line', 
                lineHeight: 1.8, 
                fontSize: '1.1rem',
                letterSpacing: '0.01em'
              }}>
                {typeof lesson.content === 'string' ? lesson.content : 'Lesson content format not supported.'}
              </Typography>
            )}

            <Divider sx={{ 
              my: 4,
              borderColor: 'divider',
              borderWidth: '1px'
            }} />
            
            {/* Navigation and Completion Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center', 
              mt: 4,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 3, sm: 2 }
            }}>
              {/* Left side - Navigation buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'space-between', sm: 'flex-start' }
              }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    const currentIndex = sidebarLessons.findIndex(lesson => lesson.id === lessonId);
                    const prevLesson = sidebarLessons[currentIndex - 1];
                    if (prevLesson && !prevLesson.isHeader) {
                      navigate(`/module-content/${moduleId}/${prevLesson.id}`);
                    }
                  }}
                  disabled={!sidebarLessons.find((lesson, index) => 
                    lesson.id === lessonId && index > 0 && !sidebarLessons[index - 1].isHeader
                  )}
                  sx={{
                    minWidth: 'auto',
                    width: { xs: '56px', sm: '48px' },
                    height: { xs: '56px', sm: '48px' },
                    borderRadius: '50%',
                    p: 0,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2
                    }
                  }}
                >
                  <ArrowBack sx={{ fontSize: { xs: '1.5rem', sm: '1.25rem' } }} />
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate(`/courses/fall-prevention`)}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 1.5 },
                    borderRadius: 2,
                    flex: { xs: 1, sm: 'none' },
                    minWidth: { xs: 0, sm: '140px' },
                    maxWidth: { xs: 'calc(100% - 72px)', sm: 'none' },
                    borderWidth: 2,
                    fontSize: { xs: '0.9rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderWidth: 2
                    }
                  }}
                >
                  Back to Module
                </Button>
          </Box>
          
              {/* Right side - Complete and Next buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'space-between', sm: 'flex-end' }
              }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleComplete}
                  disabled={isCompleted}
                  startIcon={isCompleted ? <CheckCircle /> : null}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 1.5 },
                    borderRadius: 2,
                    flex: { xs: 1, sm: 'none' },
                    minWidth: { xs: 0, sm: '140px' },
                    maxWidth: { xs: 'calc(100% - 72px)', sm: 'none' },
                    boxShadow: 2,
                    fontSize: { xs: '0.9rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  {isCompleted ? 'Completed' : 'Mark Complete'}
            </Button>

            <Button 
              variant="contained" 
              color="primary"
              onClick={() => {
                    const currentIndex = sidebarLessons.findIndex(lesson => lesson.id === lessonId);
                    const nextLesson = sidebarLessons[currentIndex + 1];
                    if (nextLesson && !nextLesson.isHeader) {
                      navigate(`/module-content/${moduleId}/${nextLesson.id}`);
                    }
                  }}
                  disabled={!sidebarLessons.find((lesson, index) => 
                    lesson.id === lessonId && 
                    index < sidebarLessons.length - 1 && 
                    !sidebarLessons[index + 1].isHeader
                  )}
                  sx={{
                    minWidth: 'auto',
                    width: { xs: '56px', sm: '48px' },
                    height: { xs: '56px', sm: '48px' },
                    borderRadius: '50%',
                    p: 0,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                >
                  <ArrowBack sx={{ 
                    transform: 'rotate(180deg)',
                    fontSize: { xs: '1.5rem', sm: '1.25rem' }
                  }} />
            </Button>
              </Box>
          </Box>
        </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ModuleContentPage;

// Remove the old mock data and diagrams if they are not needed
/*
const AssessmentDiagrams = () => { ... };
const InterventionDiagrams = () => { ... };
const ModuleDiagrams = () => { ... };
const AssessmentFlowDiagram = () => { ... };
*/
